import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import * as suboperatorsActions from './billing.suboperators.actions';
import * as suboperatorsSelectors from './billing.suboperators.selectors';
import { OperatorsService } from '../../../shared/services/operators.service';
import { MessageTranslateService } from '../../../shared/services/message.service';
import { StripeAccountState } from '../../../core/cms-api/models';
import { PartnerInfoService } from '../services/partner.info.service';

export class BillingSuboperatorsEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private operatorsService = inject(OperatorsService);
  private partnerInfoService = inject(PartnerInfoService);
  private messageService = inject(MessageTranslateService);

  initSuboperators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suboperatorsActions.initSuboperators),
      switchMap(() =>
        of(
          suboperatorsActions.loadSubCpoFilterParameters(),
          suboperatorsActions.loadSubCpos(),
        ),
      ),
    ),
  );

  loadSubCpos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        suboperatorsActions.loadSubCpos,
        suboperatorsActions.changeSubCpoFilterSuccess,
        suboperatorsActions.changeSubCpoPaginationSuccess,
        suboperatorsActions.blockSubCpoSuccess,
      ),
      concatLatestFrom(() => [
        this.store.select(suboperatorsSelectors.selectSubCpoPagination),
        this.store.select(suboperatorsSelectors.selectSubCpoCurrentFilter),
      ]),
      switchMap(([_action, pagination, filter]) =>
        this.operatorsService
          .searchSubCpos({
            pageIndex: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? 10,
            operatorIds: filter?.operatorIds ?? null,
            installDateFrom: filter?.installDates?.length
              ? filter.installDates.slice().sort()[0]
              : null,
            installDateTo: filter?.installDates?.length
              ? filter.installDates.slice().sort().at(-1)!
              : null,
            accountStatuses: (filter?.accountStatuses as StripeAccountState[]) ?? null,
            parentOperatorIds: filter?.parentOperatorIds ?? null,
          })
          .pipe(
            map((res) =>
              res.isSuccess
                ? suboperatorsActions.loadSubCposSuccess({ response: res.value! })
                : suboperatorsActions.loadSubCposFailure({ error: res.error! }),
            ),
            catchError(() =>
              of(
                suboperatorsActions.loadSubCposFailure({
                  error: { errorMessage: 'Loading sub CPOs failed' },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  loadFilterParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suboperatorsActions.loadSubCpoFilterParameters),
      switchMap(() =>
        this.operatorsService.getSubCpoFilters().pipe(
          map((res) =>
            res.isSuccess
              ? suboperatorsActions.loadSubCpoFilterParametersSuccess({
                  filterParameters: res.value!,
                })
              : suboperatorsActions.loadSubCpoFilterParametersFailure({ error: res.error! }),
          ),
          catchError(() =>
            of(
              suboperatorsActions.loadSubCpoFilterParametersFailure({
                error: { errorMessage: 'Loading filter parameters failed' },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  changeFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suboperatorsActions.changeSubCpoFilter),
      map(() => suboperatorsActions.changeSubCpoFilterSuccess()),
    ),
  );

  changePagination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suboperatorsActions.changeSubCpoPagination),
      map(() => suboperatorsActions.changeSubCpoPaginationSuccess()),
    ),
  );

  reinviteSubCpoToOnboarding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suboperatorsActions.reinviteSubCpoToOnboarding),
      switchMap(({ subCpoId }) =>
        this.partnerInfoService.reinviteSubCpoToOnboarding(subCpoId).pipe(
          map((res) =>
            res.isSuccess
              ? suboperatorsActions.reinviteSubCpoToOnboardingSuccess()
              : suboperatorsActions.reinviteSubCpoToOnboardingFailure({ error: res.error! }),
          ),
          catchError(() =>
            of(
              suboperatorsActions.reinviteSubCpoToOnboardingFailure({
                error: { errorMessage: 'Reinvite failed' },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  reinviteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(suboperatorsActions.reinviteSubCpoToOnboardingSuccess),
        tap(() =>
          this.messageService.successTranslate('billing.payouts.suboperators.reinvite.success'),
        ),
      ),
    { dispatch: false },
  );

  reinviteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(suboperatorsActions.reinviteSubCpoToOnboardingFailure),
        tap(() =>
          this.messageService.errorTranslate('billing.payouts.suboperators.reinvite.error'),
        ),
      ),
    { dispatch: false },
  );

  blockSubCpo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(suboperatorsActions.blockSubCpo),
      switchMap(({ subCpoId }) =>
        this.partnerInfoService.blockSubCpo(subCpoId).pipe(
          map((res) =>
            res.isSuccess
              ? suboperatorsActions.blockSubCpoSuccess()
              : suboperatorsActions.blockSubCpoFailure({ error: res.error! }),
          ),
          catchError(() =>
            of(
              suboperatorsActions.blockSubCpoFailure({
                error: { errorMessage: 'Block partner failed' },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  blockSubCpoSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(suboperatorsActions.blockSubCpoSuccess),
        tap(() =>
          this.messageService.successTranslate('billing.payouts.suboperators.block.success'),
        ),
      ),
    { dispatch: false },
  );

  blockSubCpoFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(suboperatorsActions.blockSubCpoFailure),
        tap(() =>
          this.messageService.errorTranslate('billing.payouts.suboperators.block.error'),
        ),
      ),
    { dispatch: false },
  );
}
